<template>
  <div
    class="cos-calendar bg-neutral-white flex flex-wrap w-full"
    :class="{ 'cos-calendar--gridless': gridless }"
  >
    <div
      ref="fullCalendarHeaderRef"
      class="cos-calendar__header sticky top-0 z-3 bg-neutral-white w-full"
    >
      <div v-if="!calendarOptions.headerToolbar" class="cos-calendar__toolbar px-6">
        <div class="cos-calendar__view-controls">
          <div v-if="$slots.action" class="flex items-center">
            <slot name="action" />
          </div>
          <div class="cos-calendar__date-nav">
            <IconButton data-test="calendar-prev" @click="goToPrevPage">
              <template #icon>
                <i class="material-symbols-rounded">arrow_back</i>
              </template>
            </IconButton>
            <IconButton data-test="calendar-next" @click="goToNextPage">
              <template #icon>
                <i class="material-symbols-rounded">arrow_forward</i>
              </template>
            </IconButton>
            <div
              v-if="!monthDropdownActive"
              class="cos-calendar__month"
              @click="monthDropDownVisibilityHandler = true"
            >
              <h2 class="text-heading-lg m-0">{{ currentCalendarMonth }}</h2>
              <i class="material-symbols-rounded">expand_more</i>
            </div>
            <div v-else class="flex items-center">
              <Dropdown
                ref="selectedMonthDropdownRef"
                v-model="currentCalendarMonth"
                :options="months"
                @change="handleMonthChange"
              />
              <IconButton class="ml-2" @click="monthDropDownVisibilityHandler = false">
                <template #icon>
                  <i class="material-symbols-rounded">close</i>
                </template>
              </IconButton>
            </div>
          </div>
        </div>
        <div class="cos-calendar__range-controls">
          <Button
            data-test="calendar-today"
            :label="t(`calendar.today`)"
            :class="[
              ButtonSeverityClass.SECONDARY,
              ButtonStateClass.ROUNDED,
              ButtonSizeClass.SMALL,
            ]"
            @click="goToTodaysPage"
          />
          <div class="cos-calendar__pill">
            <Dropdown
              v-model="currentCalendarView"
              :options="calendarViews"
              @change="toggleCalendarView"
            >
              <template #value="{ value }">
                <span class="cos-calendar__pill-value">
                  <i class="material-symbols-rounded">calendar_today</i>{{ value }}
                </span>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
    <div v-if="$slots.actionColumn" class="action-column sticky">
      <slot name="actionColumn" />
    </div>

    <div v-if="!loading" class="relative flex-1 z-2 pb-8">
      <FullCalendar ref="fullCalendarRef" :options="calendarOptions" />
      <p v-if="showEmptyState" class="cos-calendar__empty" data-test="calendar-empty-state">
        {{ emptyStateMessage }}
      </p>
    </div>
    <template v-else>
      <div class="flex-1 p-4">
        <Skeleton height="40px" />
        <div class="flex mt-1" style="gap: 0.25rem">
          <template v-for="column in 8" :key="column">
            <Skeleton v-if="column === 1" width="30%" height="40px" />
            <Skeleton v-else height="40px" />
          </template>
        </div>
        <template v-for="row in 5" :key="row">
          <div class="flex my-1" style="gap: 0.25rem">
            <template v-for="column in 8" :key="column">
              <Skeleton v-if="column === 1" width="30%" height="60px" />
              <Skeleton v-else height="60px" />
            </template>
          </div>
        </template>
      </div>
    </template>
  </div>
  <slot v-if="$slots.dialog" name="dialog" />
</template>

<script setup lang="ts">
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import rrulePlugin from '@fullcalendar/rrule';
import interactionPlugin from '@fullcalendar/interaction';
import { ref, onMounted, type PropType, watch, computed, nextTick } from 'vue';
import { ButtonSeverityClass, ButtonSizeClass, ButtonStateClass } from '../../buttons';
import type { CalendarOptions, EventMountArg, EventSourceInput } from '@fullcalendar/core';
import { CALENDAR_GRIDLESS } from './constants';
import { useFullCalendarActions } from './composables';
import { useElementSize, useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useContentPageStore } from '@centric-os/stores/content-page/use-content-page-store';
import { storeToRefs } from 'pinia';

const { t } = useI18n();

const props = defineProps({
  customCalendarOptions: {
    type: Object as PropType<Partial<CalendarOptions>>,
    required: false,
  },
  events: {
    type: Object as PropType<EventSourceInput>,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  gridless: {
    type: Boolean,
    required: false,
    default: CALENDAR_GRIDLESS,
  },
  emptyMessage: {
    type: String,
    required: false,
    default: '',
  },
  emptyDayMessage: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['eventClicked']);

// Empty state: FullCalendar knows which event segments are in the visible range, so count the
// foreground ones it puts on screen. Closures are background events and do not count. Keyed by
// element so a repeated unmount is harmless.
const mountedEventElements = new Set<HTMLElement>();
const mountedEventCount = ref(0);
const trackEventMount = ({ el, event }: EventMountArg) => {
  if (event.display === 'background') return;
  mountedEventElements.add(el);
  mountedEventCount.value = mountedEventElements.size;
};
const trackEventUnmount = ({ el }: EventMountArg) => {
  mountedEventElements.delete(el);
  mountedEventCount.value = mountedEventElements.size;
};
// Day view gets its own wording when the page provides one.
const emptyStateMessage = computed(() =>
  calendarOptions.value.initialView === 'timeGridDay' && props.emptyDayMessage
    ? props.emptyDayMessage
    : props.emptyMessage,
);
const showEmptyState = computed(
  () => Boolean(props.emptyMessage) && !props.loading && mountedEventCount.value === 0,
);
watch(
  () => props.loading,
  (isLoading) => {
    if (!isLoading) return;
    mountedEventElements.clear();
    mountedEventCount.value = 0;
  },
);

const calendarOptions = ref<CalendarOptions>({
  plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, rrulePlugin],
  timeZone: 'UTC',
  initialView: 'timeGridWeek',
  weekends: true,
  slotDuration: '1:00:00',
  slotEventOverlap: false,
  expandRows: true,
  height: 'auto',
  headerToolbar: false,
  allDayText: '',
  dayHeaderFormat: { weekday: 'long', day: 'numeric' },
  eventOrderStrict: true,
  eventTimeFormat: {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: true,
  },
  events: [],
  eventDidMount: trackEventMount,
  eventWillUnmount: trackEventUnmount,
});

const fullCalendarRef = ref<InstanceType<typeof FullCalendar>>();

const currentCalendarMonth = ref<string>();

const months = ref<string[]>([]);

const selectedMonthDropdownRef = ref(null);
const monthDropdownActive = ref(false);

const contentPageStore = useContentPageStore();
const { contentPageHeaderHeight } = storeToRefs(contentPageStore);

const monthDropDownVisibilityHandler = computed({
  get() {
    return monthDropdownActive.value;
  },
  async set(newValue) {
    monthDropdownActive.value = newValue;
    if (newValue) {
      await nextTick();
      selectedMonthDropdownRef.value.$el?.children?.[0]?.click();
    }
  },
});

// @ts-expect-error FIXME: Parameter 'data' implicitly has 'any' type
const handleMonthChange = (data) => {
  goToMonth(data);
  monthDropDownVisibilityHandler.value = false;
};

const {
  getMonths,
  fullCalendarViews,
  calendarViews,
  syncDate,
  goToMonth,
  toggleCalendarView,
  goToPrevPage,
  goToNextPage,
  goToTodaysPage,
} = useFullCalendarActions(fullCalendarRef, calendarOptions, currentCalendarMonth);
const currentCalendarView = ref(fullCalendarViews.WEEK);

watch(
  () => props.customCalendarOptions,
  (newVal) => {
    if (newVal) {
      const plugins = calendarOptions.value.plugins;
      const newOptions: CalendarOptions = props.customCalendarOptions;

      calendarOptions.value = {
        plugins,
        ...newOptions,
        eventDidMount: trackEventMount,
        eventWillUnmount: trackEventUnmount,
        eventClick(fcEvent) {
          emit('eventClicked', fcEvent?.event);
        },
      };
    }
  },
  { immediate: true },
);
watch(
  () => props.events,
  async (newVal) => {
    if (newVal) {
      calendarOptions.value.events = newVal;
    }
  },
  { immediate: true },
);

onMounted(() => {
  syncDate();
  months.value = getMonths();
});
// Style
const fullCalendarHeaderRef = ref();
const { height: fullCalendarHeaderHeight } = useElementSize(fullCalendarHeaderRef, undefined, {
  box: 'border-box',
});
const calendarDateAxisTopPosition = computed(() => {
  // FC makes the Date axis sticky on default, this is to adjust its top pos from 0 to...
  return `${fullCalendarHeaderHeight.value + 1}px`;
});
const { height: windowHeight } = useWindowSize();
const actionColumnHeight = computed(() => {
  // Fill the scroll container (100vh - page header) below the calendar header. Must not exceed it:
  // a sticky element taller than the room left in its parent gets pushed up with the calendar.
  return `${windowHeight.value - contentPageHeaderHeight.value - fullCalendarHeaderHeight.value - 1}px`;
});
</script>

<style lang="scss">
.cos-calendar .fc .fc-bg-event.station-closure {
  opacity: 1;
  background-size: auto auto;
  background-color: var(--color-surface-raised);
  background-image: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 20px,
    var(--color-border-subtle) 20px,
    var(--color-border-subtle) 21px
  );
  cursor: pointer;
}
.fc {
  .fc-timegrid-col-bg {
    z-index: 4;
  }
  .fc-timegrid-slot-label {
    vertical-align: top !important;
  }
  .fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > * {
    top: v-bind('calendarDateAxisTopPosition');
    &::before {
      // There is a 1px gap between FC header && our custom header.
      // Can see events scroll underneath it in said gap.
      // Removing 1px from calendarDateAxisTopPosition causes content shift.
      // This ::before hides that gap.
      content: '';
      height: 3px;
      top: -2px;
      background: white;
      position: absolute;
      width: 100%;
      left: 0;
    }
  }
  table {
    width: 100% !important;
  }
  .fc-col-header,
  .fc-timegrid-body,
  .fc-daygrid-body {
    width: 100% !important;
  }
}
.action-column {
  height: v-bind('actionColumnHeight') !important;
  max-height: v-bind('actionColumnHeight') !important;
  top: v-bind('calendarDateAxisTopPosition') !important;
}
.cos-calendar__empty {
  position: absolute;
  top: var(--space-96);
  left: var(--size-48);
  right: 0;
  margin: 0;
  text-align: center;
  pointer-events: none;
  font-size: var(--size-12);
  line-height: var(--size-16);
  font-weight: var(--font-weight-400);
  color: var(--color-text-secondary);
}
.cos-calendar__header {
  padding-bottom: var(--space-16);
}
.cos-calendar__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: var(--space-24);
  border-bottom: var(--border-width-emphasis) solid var(--color-border-section);
  .material-symbols-rounded {
    font-size: var(--size-icon-md);
  }
}
.cos-calendar__view-controls {
  display: flex;
  align-items: center;
  gap: var(--space-48);
}
.cos-calendar__date-nav {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  // Ghost icon-buttons keep a dark glyph in every state (Figma icon-button Ghost); the button
  // theme still owns the hover container.
  .p-button.p-button-icon-only,
  .p-button.p-button-icon-only:enabled:hover,
  .p-button.p-button-icon-only:enabled:active {
    color: var(--color-text-primary);
  }
}
.cos-calendar__month {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  height: var(--size-40);
  padding: 0 var(--space-8);
  color: var(--color-text-primary);
  cursor: pointer;
}
.cos-calendar__range-controls {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}
// Today and Week are Pantry secondary buttons: tinted fill, blue content, no border, matching
// the height of the prev/next icon buttons and month control beside them in this toolbar.
// The global button and dropdown rules set height, padding and radius with !important, so the
// values below have to as well.
.cos-calendar__range-controls .p-button.p-button-secondary {
  height: var(--size-40);
  min-height: var(--size-40) !important;
  padding: 0 var(--space-12) !important;
  border: 0;
  background: var(--blue-50);
  color: var(--color-text-link);
  font-size: var(--size-14);
  line-height: var(--size-20);
  .p-button-label {
    font-weight: var(--font-weight-500);
  }
  // States match the app's secondary buttons so the toolbar stays consistent.
  &:enabled:hover {
    background: var(--blue-100);
    color: var(--blue-600);
  }
  &:enabled:active {
    background: var(--blue-200);
    color: var(--blue-800);
  }
  &:enabled:focus {
    box-shadow: none;
  }
  &:enabled:focus-visible {
    outline: var(--border-width-emphasis) solid var(--amber-800);
    outline-offset: calc(var(--border-width-emphasis) * -1);
    box-shadow: none;
  }
}
// Week pill: matches the Today button's height and left/right padding. 4 between icon/label/
// chevron. The chevron keeps the global dropdown size so it matches every other dropdown.
// The :not() matches the global dropdown rule's specificity so this one wins the tie.
.cos-calendar__pill .p-dropdown:not(.p-paginator-rpp-options) {
  height: var(--size-40);
  min-height: var(--size-40) !important;
  align-items: center;
  gap: var(--space-4);
  padding: 0 var(--space-8);
  border: 0;
  border-radius: var(--radius-role-full) !important;
  background: var(--blue-50);
  .p-dropdown-label,
  .p-dropdown-trigger {
    color: var(--color-text-link);
  }
  &:not(.p-disabled):hover {
    background: var(--blue-100);
    .p-dropdown-label,
    .p-dropdown-trigger {
      color: var(--blue-600);
    }
  }
  &.p-overlay-open {
    background: var(--blue-200);
    .p-dropdown-label,
    .p-dropdown-trigger {
      color: var(--blue-800);
    }
  }
  &:not(.p-disabled).p-focus {
    box-shadow: none;
  }
  &:not(.p-disabled):has(:focus-visible) {
    outline: var(--border-width-emphasis) solid var(--amber-800);
    outline-offset: calc(var(--border-width-emphasis) * -1);
  }
  .p-dropdown-label {
    padding: 0;
    font-size: var(--size-14);
    line-height: var(--size-20);
    font-weight: var(--font-weight-500);
  }
  .p-dropdown-trigger {
    width: auto;
    .material-symbols-rounded {
      font-size: var(--size-icon-sm);
    }
  }
}
.cos-calendar__pill-value {
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  // .p-dropdown-label is display:block, so this inline-flex child default to
  // vertical-align: baseline — that reserves descender space below it (the
  // usual inline-level quirk), making the label's own box taller than its
  // content and pushing the visible icon/text up within it. vertical-align
  // only matters for inline-level boxes, so setting it here is enough.
  vertical-align: top;
  .material-symbols-rounded {
    font-size: var(--size-icon-sm);
    // PrimeVue's Dropdown wraps itself in a .p-field, so this icon inherits
    // .p-field .material-symbols-rounded's line-height: 0.5 (meant for form-field
    // adornment icons) — that shrinks its line box and throws off centering
    // against the label text. Restore the normal box so align-items: center works.
    line-height: 1 !important;
  }
}
</style>
